"use client"

import React, {useEffect, useRef} from "react";
import {toast} from "sonner";
import {useRouter} from "next/navigation";
import {usePendingJobs} from "@/hooks/queries/use-pending-jobs";
import {projectPath} from "@/app/paths";
import {PendingJob} from "@/pb/api/devplan/types/jobs";

function jobTitle(job: PendingJob): string {
  if (job.jobType === "prototypeWalkthrough") return "Prototype walkthrough";
  if (job.jobType === "quickWinInit") return "Setting up your feature";
  if (job.jobType === "catalogFeedback") return "Applying catalog feedback";
  if (job.jobType === "projectIntake") return "Processing attached context";
  return `Job: ${job.jobType}`;
}

function jobDescription(job: PendingJob, action?: React.ReactNode): React.ReactNode {
  if (job.jobType === "prototypeWalkthrough") {
    const wt = job.details?.$case === "prototypeWalkthrough" ? job.details.value : undefined;
    const lines: React.ReactNode[] = [];
    if (wt?.prototypeUrl) {
      let label: string;
      try {
        label = new URL(wt.prototypeUrl).hostname;
      } catch {
        label = wt.prototypeUrl;
      }
      lines.push(
        <a
          key="url"
          href={wt.prototypeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="underline text-foreground/80 hover:text-foreground"
        >
          {label}
        </a>
      );
    }
    if (wt?.userRequest) {
      const truncated = wt.userRequest.length > 80
        ? wt.userRequest.slice(0, 77) + "..."
        : wt.userRequest;
      lines.push(<span key="req" className="text-muted-foreground">{truncated}</span>);
    }
    if (action) lines.push(<React.Fragment key="action">{action}</React.Fragment>);
    if (lines.length === 0) return undefined;
    return <span className="flex flex-col gap-1">{lines}</span>;
  }
  if (job.jobType === "quickWinInit") {
    const qw = job.details?.$case === "quickWinInit" ? job.details.value : undefined;
    const lines: React.ReactNode[] = [];
    if (qw?.description) {
      const truncated = qw.description.length > 80
        ? qw.description.slice(0, 77) + "..."
        : qw.description;
      lines.push(<span key="desc" className="text-muted-foreground">{truncated}</span>);
    }
    if (action) lines.push(<React.Fragment key="action">{action}</React.Fragment>);
    if (lines.length === 0) return undefined;
    return <span className="flex flex-col gap-1">{lines}</span>;
  }
  if (job.jobType === "catalogFeedback") {
    const cf = job.details?.$case === "catalogFeedback" ? job.details.value : undefined;
    const lines: React.ReactNode[] = [];
    if (cf?.comment) {
      // Roughly 5 lines × ~80 chars per line in the toast width.
      const maxChars = 400;
      const truncated = cf.comment.length > maxChars
        ? cf.comment.slice(0, maxChars - 1) + "…"
        : cf.comment;
      lines.push(
        <span
          key="comment"
          className="text-muted-foreground italic line-clamp-5 break-words"
        >
          “{truncated}”
        </span>
      );
    }
    if (action) lines.push(<React.Fragment key="action">{action}</React.Fragment>);
    if (lines.length === 0) return undefined;
    return <span className="flex flex-col gap-1">{lines}</span>;
  }
  if (job.jobType === "projectIntake") {
    const sp = job.details?.$case === "projectIntake" ? job.details.value : undefined;
    const lines: React.ReactNode[] = [];
    if (sp?.attachmentIds?.length) {
      lines.push(
        <span key="count" className="text-muted-foreground">
          {sp.attachmentIds.length} item{sp.attachmentIds.length === 1 ? "" : "s"} attached
        </span>
      );
    }
    if (action) lines.push(<React.Fragment key="action">{action}</React.Fragment>);
    if (lines.length === 0) return undefined;
    return <span className="flex flex-col gap-1">{lines}</span>;
  }
  return undefined;
}

export function usePendingJobToasts(companyId: number | undefined) {
  const {jobs} = usePendingJobs(companyId);
  const router = useRouter();
  const shownStatuses = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    for (const job of jobs) {
      const toastId = `pending-job-${job.id}`;
      const previousStatus = shownStatuses.current.get(job.id);

      if (previousStatus === job.status) continue;
      shownStatuses.current.set(job.id, job.status);

      const title = jobTitle(job);
      const projectId = job.details?.$case === "prototypeWalkthrough" ? job.details.value.projectId
        : job.details?.$case === "quickWinInit" ? job.details.value.projectId
        : job.details?.$case === "projectIntake" ? job.details.value.projectId
        : undefined;

      if (job.status === "pending") {
        toast.loading(title, {
          id: toastId,
          duration: Infinity,
          closeButton: true,
          description: jobDescription(job),
          // Sonner's loading toast hides the closeButton behind the spinner;
          // add an explicit Dismiss action so the user can always close it.
          cancel: {label: "Dismiss", onClick: () => toast.dismiss(toastId)},
        });
      } else if (previousStatus === "pending" && job.status === "completed") {
        const actionButton = projectId && companyId ? (
          <button
            className="mt-1 text-sm font-medium underline underline-offset-2 text-foreground hover:text-foreground/80"
            onClick={() => router.push(projectPath(companyId, projectId))}
          >
            Open project
          </button>
        ) : undefined;
        toast.success(title, {
          id: toastId,
          duration: Infinity,
          closeButton: true,
          description: jobDescription(job, actionButton),
        });
      } else if (previousStatus === "pending" && job.status === "failed") {
        toast.error(title, {id: toastId, duration: Infinity, closeButton: true, description: jobDescription(job)});
      }
    }
  }, [jobs, companyId, router]);
}
